import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Tooltip,
  Stack,
  Box,
  TableCell as MuiTableCell
} from '@mui/material';
import { Star, RateReview } from '@mui/icons-material';
import { DateRange } from '../../types/dateRange';
import Title from '../../components/Title';
import { formatDate } from '../../resources/functions';
import TableCell from '../../components/TableCell';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import {
  getFeedbacksAsync,
  selectFeedbacks
} from '../../redux/slices/feedbackSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { Moment } from 'moment';
import FilterHeader, {
  FilterHeaderValues
} from '../../components/Header/FilterHeader';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const PREFIX = 'FeedbacksPage';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)',
  [`& .${classes.tableContainer}`]: {
    marginTop: 20,
    marginBottom: 20
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const initialValues = {
  scooterName: '',
  userId: '',
  dateRange: [null, null] as DateRange<Moment | null>
};

const getRatingConfig = (rate: number, t: TFunction) => {
  if (rate >= 5) {
    return {
      backgroundColor: 'rgba(76, 175, 80, 0.12)',
      iconColor: '#4CAF50',
      tooltip: t('feedbacks.ratingExcellent')
    };
  } else if (rate >= 4) {
    return {
      backgroundColor: 'rgba(139, 195, 74, 0.12)',
      iconColor: '#8BC34A',
      tooltip: t('feedbacks.ratingGood')
    };
  } else if (rate >= 3) {
    return {
      backgroundColor: 'rgba(78, 205, 196, 0.12)',
      iconColor: '#4ECDC4',
      tooltip: t('feedbacks.ratingFair')
    };
  } else if (rate >= 2) {
    return {
      backgroundColor: 'rgba(255, 152, 0, 0.12)',
      iconColor: '#FF9800',
      tooltip: t('feedbacks.ratingPoor')
    };
  } else {
    return {
      backgroundColor: 'rgba(244, 67, 54, 0.12)',
      iconColor: '#F44336',
      tooltip: t('feedbacks.ratingVeryPoor')
    };
  }
};

const FeedbacksPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);
  const isSuperAdmin = auth.info?.type === 'superAdmin';

  const cityId = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, cities.items]);

  const [filters, setFilters] =
    useState<Partial<FilterHeaderValues>>(initialValues);

  const { limit, total, items, loading } = useSelector(selectFeedbacks);
  const [page, setPage] = useState(1);

  const onChangePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  useEffect(() => {
    dispatch(
      getFeedbacksAsync({ page, limit, cityId: cityId ?? '', ...filters })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, cityId, filters]);

  const handleFormSubmit = (values: Partial<FilterHeaderValues>) => {
    const isValidDateRange =
      values.dateRange?.[0]?.isValid?.() && values.dateRange?.[1]?.isValid?.();

    const dateFrom = isValidDateRange
      ? values.dateRange![0]!.format(DATE_FORMAT)
      : undefined;

    const dateTo = isValidDateRange
      ? values.dateRange![1]!.format(DATE_FORMAT)
      : undefined;

    const dateObj = isValidDateRange
      ? {
          dateTo: dateTo as string,
          dateFrom: dateFrom as string
        }
      : {};

    setFilters({
      scooterName: values?.scooterName?.length ? values.scooterName : undefined,
      userId: values?.userId?.length ? values.userId : undefined,
      ...dateObj
    });
  };

  return (
    <Root>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <Grid container direction='column'>
          <Grid item>
            <Title>{t('feedbacks.title')}</Title>
          </Grid>
          <Grid item>
            <FilterHeader
              initialValues={initialValues}
              onSubmit={handleFormSubmit}
            />
          </Grid>
        </Grid>
        {!loading && items?.length === 0 ? (
          <Grid
            container
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            marginTop={'50px'}
          >
            <Typography>{t('feedbacks.empty')}</Typography>
          </Grid>
        ) : null}
        {Boolean(items?.length) && (
          <div>
            <TableContainer className={classes.tableContainer} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableHeaderRow>
                  <TableHeaderCell>{t('feedbacks.colScooter')}</TableHeaderCell>
                  <TableHeaderCell>{t('feedbacks.colRating')}</TableHeaderCell>
                  <TableHeaderCell>{t('feedbacks.colText')}</TableHeaderCell>
                  <TableHeaderCell>{t('feedbacks.colUser')}</TableHeaderCell>
                  {isSuperAdmin && <TableHeaderCell>{t('feedbacks.colDevice')}</TableHeaderCell>}
                  <TableHeaderCell>{t('feedbacks.colDate')}</TableHeaderCell>
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {items?.map(feedback => {
                  const ratingConfig = getRatingConfig(feedback.rate, t);
                  
                  return (
                    <StyledTableRow key={feedback._id}>
                      <TableCell>{feedback.scooter.name}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip title={ratingConfig.tooltip} arrow>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: ratingConfig.backgroundColor,
                                borderRadius: '8px'
                              }}
                            >
                              <Star sx={{ fontSize: 24, color: ratingConfig.iconColor }} />
                            </Avatar>
                          </Tooltip>
                          <Typography variant="body2">{feedback.rate}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{feedback.description}</TableCell>
                    {/* <TableCell>{feedback?.city?.cityName}</TableCell> */}
                    <TableCell>
                      {feedback.user.firstName}
                      <br />
                      {feedback.user.phone}
                    </TableCell>
                    {isSuperAdmin ? (
                      <TableCell>
                        {feedback?.device?.replace(/,/g, '\n') ?? t('feedbacks.noDeviceData')}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      {formatDate(feedback.createdAt, { time: true })}
                    </TableCell>
                  </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      {!loading && items?.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
          borderRadius: 3,
          mt: 2
        }}>
          <Avatar sx={{ 
            width: 64, 
            height: 64, 
            bgcolor: 'rgba(80, 115, 248, 0.12)', 
            margin: '0 auto 16px',
            fontSize: 32
          }}>
            <RateReview sx={{ fontSize: 40, color: '#5073F8' }} />
          </Avatar>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {t('feedbacks.empty')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('feedbacks.emptyHint')}
          </Typography>
        </Box>
      )}

      {total > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={total} 
            page={page}
            onChange={onChangePage}
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 600,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
                  color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? '#212121' : '#E0E0E0'
                  }
                }
              }
            }}
          />
        </Box>
      )}
      </Paper>
    </Root>
  );
};

export default FeedbacksPage;
